<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { computed } from 'vue';
import { useTimezoneStore } from '@/stores/timezone';
import { formatTimestamp } from '@/utils/dt';

interface Props {
    message: string
    boldSegments: string[]
    createdAt: number
    isRead: boolean
    href: string | null
}

const props = defineProps<Props>();
const emit = defineEmits<{ click: [] }>();

const tzStore = useTimezoneStore();

const formattedTimestamp = computed(() =>
    formatTimestamp(new Date(props.createdAt * 1000), tzStore.effectiveTimezone),
);

const renderedSegments = computed(() => {
    const { message, boldSegments } = props;
    if (!boldSegments.length) return [{ text: message, bold: false }];

    const pattern = boldSegments
        .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');
    const parts = message.split(regex);

    const boldLower = new Set(boldSegments.map((s) => s.toLowerCase()));

    return parts
        .filter((p) => p.length > 0)
        .map((p) => ({
            text: p,
            bold: boldLower.has(p.toLowerCase()),
        }));
});
</script>

<template>
    <div
        class="notification-entry"
        :class="{
            'notification-entry--read': isRead,
            'notification-entry--clickable': !!href,
        }"
        role="button"
        :tabindex="href ? 0 : undefined"
        @click="emit('click')"
        @keydown.enter="emit('click')"
    >
        <div class="notification-entry__content">
            <span v-if="!isRead" class="notification-entry__dot" />

            <div class="notification-entry__body">
                <p class="notification-entry__message">
                    <template v-for="(seg, idx) in renderedSegments" :key="idx">
                        <strong v-if="seg.bold">{{ seg.text }}</strong>
                        <template v-else>{{ seg.text }}</template>
                    </template>
                </p>

                <span class="notification-entry__timestamp">{{ formattedTimestamp }}</span>
            </div>
        </div>

        <hr class="notification-entry__separator" />
    </div>
</template>

<style lang="scss" scoped>
.notification-entry {
    padding: 0;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
            to top,
            rgba($main-orange, 0.08),
            transparent 70%
        );
        clip-path: inset(100% 0 0 0);
        transition: clip-path 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: none;
    }

    &--clickable {
        cursor: pointer;

        &:hover::after {
            clip-path: inset(0);
        }
    }

    &--read {
        opacity: 0.55;
    }

    &__content {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        padding: 0 0 0 0;
    }

    &__dot {
        flex-shrink: 0;
        width: 7px;
        height: 7px;
        margin-top: 8px;
        border-radius: 50%;
        background: $toolbar-icon-button-hover-border-color;
    }

    &__body {
        flex: 1;
        min-width: 0;
    }

    &__message {
        margin: 0;
        font-family: $font-family-archivo;
        font-style: normal;
        font-weight: 300;
        font-size: 15px;
        line-height: 24px;
        color: $black-color;

        strong {
            font-weight: 700;
        }
    }

    &__timestamp {
        display: block;
        font-family: $font-family-archivo;
        font-style: normal;
        font-weight: 300;
        font-size: 13px;
        line-height: 24px;
        color: rgba($black-color, 0.5);
    }

    &__separator {
        margin: 15px 0 0;
        border: none;
        border-top: 1px solid $border-gray-color;
    }
}
</style>
